import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../authService';
import { supabase } from '../supabaseClient';
import { systemAccessService } from '../services/systemAccessService';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Container,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Tooltip,
  Stack,
  Divider,
  ListItemIcon,
  Paper
} from '@mui/material';
import {
  WaterDrop,
  Apartment,
  BusinessCenter,
  MonetizationOn,
  HealthAndSafety,
  CardMembership,
  MenuBook,
  Settings,
  Logout,
  Person,
  Lock,
  ArrowForward,
  Info,
  AccountBalance
} from '@mui/icons-material';
import { showNotification } from './Notification';
import EventGreetingModal from './common/EventGreetingModal';
import ManageEventsDialog from './common/ManageEventsDialog';
import { eventService } from '../services/eventService';
import MobileBottomBar from './common/MobileBottomBar';
import ProfileModal from './modals/ProfileModal';

const SystemsPortal = ({ onLogout }) => {
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [allowedSystems, setAllowedSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showGreeting, setShowGreeting] = useState(false);
  const [showManageEvents, setShowManageEvents] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    document.title = 'Systems Portal - LGU Concepcion';
    const checkUserAndFetchSystems = async () => {
      let currentUser = authService.getUserSession();
      
      if (!currentUser) {
        navigate('/login');
        return;
      }

      // Verify user department from DB to ensure fresh data
      try {
        const { data: userProfile, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('userid', currentUser.userid)
          .single();

        if (userProfile && !userError) {
          // Update local session if department changed
          if (userProfile.department !== currentUser.department) {
            console.log('Updating stale department:', currentUser.department, '->', userProfile.department);
            currentUser = { ...currentUser, department: userProfile.department, role: userProfile.role };
            authService.setUserSession(currentUser);
          }
        }
      } catch (err) {
        console.error('Error verifying user profile:', err);
      }

      setUser(currentUser);
      fetchAllowedSystems(currentUser);
    };

    // Fetch active event
    const loadEvent = async () => {
      const event = await eventService.getActiveEvent();
      if (event) {
        // Check if user suppressed this specific event
        const isHidden = sessionStorage.getItem(`hideEvent_${event.id}`);
        if (!isHidden) {
          setActiveEvent(event);
          setShowGreeting(true);
        }
      }
    };

    loadEvent();
    checkUserAndFetchSystems();
  }, [navigate]);

  const fetchAllowedSystems = async (currentUser) => {
    setLoading(true);
    try {
      const allowed = await systemAccessService.getAllowedSystemsForUser(currentUser);
      setAllowedSystems(allowed);
    } catch (error) {
      console.error('Error fetching allowed systems:', error);
      setAllowedSystems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    setShowProfileModal(true);
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
    authService.setUserSession(updatedUser);
  };

  const handleLogout = () => {
    handleMenuClose();
    authService.logout();
    if (onLogout) {
      onLogout();
    }
    navigate('/login', { replace: true });
  };

  const systems = [
    {
      id: 'water-billing',
      name: 'Water Billing System',
      description: 'Manage water consumption billing and payments',
      icon: <WaterDrop fontSize="large" />,
      color: '#3b82f6',
      path: '/portal/water-billing/dashboard',
      available: true
    },
    {
      id: 'property-tax',
      name: 'Real Property Tax System',
      description: 'Real property tax assessment and collection',
      icon: <Apartment fontSize="large" />,
      color: '#10b981',
      path: '/portal/property-tax/dashboard',
      available: true
    },
    {
      id: 'business-permit',
      name: 'Business Permit & Licensing',
      description: 'Business registration and permit processing',
      icon: <BusinessCenter fontSize="large" />,
      color: '#f59e0b',
      path: '/portal/business-permit/dashboard',
      available: true
    },
    {
      id: 'treasury',
      name: 'Treasury Management',
      description: 'Revenue collection and financial management',
      icon: <MonetizationOn fontSize="large" />,
      color: '#8b5cf6',
      path: '/portal/treasury',
      available: false
    },
    {
      id: 'rural-health',
      name: 'Rural Health Unit',
      description: 'Health services, patient records, and medical management',
      icon: <HealthAndSafety fontSize="large" />,
      color: '#ef4444',
      path: '/portal/rural-health/dashboard',
      available: true
    },
    {
      id: 'civil-registry',
      name: 'Municipal Civil Registry',
      description: 'Birth, marriage, death certificates, and CENOMAR',
      icon: <CardMembership fontSize="large" />,
      color: '#667eea',
      path: '/portal/civil-registry/home',
      available: true
    },
    {
      id: 'e-logbook',
      name: 'e-LogBook',
      description: 'Track documents, vouchers, CAFOA, checks, and more',
      icon: <MenuBook fontSize="large" />,
      color: '#f97316',
      path: '/portal/e-logbook/home',
      available: true
    },
    {
      id: 'settings',
      name: 'System Settings',
      description: 'Manage users, departments, and system configuration',
      icon: <Settings fontSize="large" />,
      color: '#6b7280',
      path: '/portal/settings/users',
      available: true
    }
  ];

  const filteredSystems = systems.filter(system => system.available);

  const handleSystemClick = (system) => {
    // Final safety check for Accounting
    if (user?.department?.toLowerCase().includes('accounting') && system.id === 'business-permit') {
      showNotification('Access Denied: Restricted for Accounting Department', 'error');
      return;
    }

    const isAllowed = allowedSystems.includes(system.id);
    if (system.available && isAllowed) {
      sessionStorage.setItem('currentSystem', system.id);
      sessionStorage.setItem('currentSystemColor', system.color);
      navigate(system.path);
    }
  };

  if (!user || loading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8faf9' }}>
      {/* Top Header */}
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2.5, 
                  bgcolor: '#059669', 
                  color: '#ffffff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  boxShadow: '0 4px 10px rgba(5, 150, 105, 0.25)'
                }}
              >
                <AccountBalance sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
                  LGU Concepcion
                </Typography>
                <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>
                  Online Systems Portal & ERP
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                  {user.firstname} {user.lastname}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  {user.department || user.position || user.role}
                </Typography>
              </Box>

              <IconButton 
                onClick={handleMenuOpen} 
                sx={{ 
                  p: 0.5,
                  border: '2px solid rgba(5, 150, 105, 0.2)',
                  borderRadius: 3
                }}
              >
                <Avatar sx={{ bgcolor: '#059669', width: 36, height: 36, fontSize: '0.875rem', fontWeight: 700 }}>
                  {user.firstname?.charAt(0)}{user.lastname?.charAt(0)}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    minWidth: 190,
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e2e8f0',
                    p: 0.5
                  }
                }}
              >
                <MenuItem onClick={handleProfileClick} sx={{ borderRadius: 1.5, py: 1 }}>
                  <ListItemIcon>
                    <Person fontSize="small" sx={{ color: '#059669' }} />
                  </ListItemIcon>
                  User Profile
                </MenuItem>
                {user?.role === 'superadmin' && (
                  <MenuItem onClick={() => { handleMenuClose(); setShowManageEvents(true); }} sx={{ borderRadius: 1.5, py: 1 }}>
                    <ListItemIcon>
                      <Settings fontSize="small" sx={{ color: '#059669' }} />
                    </ListItemIcon>
                    Manage Events
                  </MenuItem>
                )}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={handleLogout} sx={{ borderRadius: 1.5, py: 1, color: '#ef4444' }}>
                  <ListItemIcon>
                    <Logout fontSize="small" sx={{ color: '#ef4444' }} />
                  </ListItemIcon>
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 5 }, pb: { xs: 10, sm: 10, md: 6 } }}>
        {/* Welcome Hero Banner */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 3.5, md: 4 },
            mb: { xs: 3, md: 4.5 },
            borderRadius: 4,
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -2px rgba(0, 0, 0, 0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2.5,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', bgcolor: '#059669' }} />
          
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="800" color="#0f172a">
                Welcome back, {user.firstname}!
              </Typography>
              <Chip 
                label={user.role ? user.role.toUpperCase() : 'STAFF'}
                size="small"
                sx={{
                  bgcolor: 'rgba(5, 150, 105, 0.12)',
                  color: '#059669',
                  fontWeight: 700,
                  fontSize: '0.6875rem',
                  border: '1px solid rgba(5, 150, 105, 0.25)'
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Department: <strong>{user.department || 'Municipal Staff'}</strong> • Select an authorized system to launch your dedicated workspace.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${allowedSystems.length} Systems Authorized`}
              size="small"
              sx={{ 
                bgcolor: '#f1f5f9', 
                color: '#334155', 
                fontWeight: 700, 
                fontSize: '0.75rem',
                border: '1px solid #e2e8f0',
                py: 1.75
              }}
            />
          </Box>
        </Paper>

        {isMobile ? (
          /* Mobile & Tablet App Launcher View */
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: 4,
              bgcolor: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.04)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, pb: 1.5, borderBottom: '1px solid #f1f5f9' }}>
              <Typography variant="caption" fontWeight={800} color="#64748b" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                SYSTEM LAUNCHER
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Tap to open
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 2.5, sm: 3 }} justifyContent="flex-start" alignItems="center">
              {filteredSystems.length === 0 ? (
                <Grid item xs={12}>
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Lock sx={{ fontSize: 48, color: '#94a3b8', mb: 1.5 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                      No Systems Assigned
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Contact your system administrator to assign module permissions.
                    </Typography>
                  </Box>
                </Grid>
              ) : (
                filteredSystems.map((system) => {
                  const isAllowed = allowedSystems.includes(system.id);
                  return (
                    <Grid item xs={4} sm={3} key={system.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Tooltip title={system.name} arrow placement="top">
                        <Box
                          onClick={() => handleSystemClick(system)}
                          sx={{
                            position: 'relative',
                            width: { xs: 64, sm: 72 },
                            height: { xs: 64, sm: 72 },
                            borderRadius: 3.5,
                            bgcolor: isAllowed ? '#ffffff' : '#f8fafc',
                            border: `2px solid ${isAllowed ? system.color : '#e2e8f0'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: isAllowed ? `0 8px 16px -4px ${system.color}30` : 'none',
                            color: isAllowed ? system.color : '#94a3b8',
                            cursor: isAllowed ? 'pointer' : 'not-allowed',
                            userSelect: 'none',
                            transition: 'all 0.2s ease',
                            opacity: isAllowed ? 1 : 0.6,
                            mb: 1,
                            '&:hover': isAllowed ? { transform: 'translateY(-3px)', boxShadow: `0 12px 20px -4px ${system.color}45` } : {},
                            '&:active': isAllowed ? { transform: 'scale(0.94)' } : {},
                            '& .MuiSvgIcon-root': {
                              fontSize: { xs: '1.85rem', sm: '2.1rem' }
                            }
                          }}
                        >
                          {system.icon}
                          {!isAllowed && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                bgcolor: '#64748b',
                                color: 'white',
                                borderRadius: '50%',
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid white'
                              }}
                            >
                              <Lock sx={{ fontSize: '0.7rem !important' }} />
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 700, 
                          color: isAllowed ? '#0f172a' : '#94a3b8',
                          fontSize: '0.7rem',
                          lineHeight: 1.2,
                          maxWidth: 80,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {system.name}
                      </Typography>
                    </Grid>
                  );
                })
              )}
            </Grid>
          </Paper>
        ) : (
          /* Desktop Card Grid View */
          <Grid container spacing={3}>
            {filteredSystems.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                  <Lock sx={{ fontSize: 56, color: '#94a3b8', mb: 2 }} />
                  <Typography variant="h6" fontWeight={700} color="#0f172a">
                    No Systems Assigned
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please contact your system administrator to assign module access.
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              filteredSystems.map((system) => {
                const isAllowed = allowedSystems.includes(system.id);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={system.id}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 3.5,
                        border: '1px solid',
                        borderColor: isAllowed ? '#e2e8f0' : '#f1f5f9',
                        bgcolor: '#ffffff',
                        transition: 'all 0.25s ease',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                        '&:hover': isAllowed ? {
                          transform: 'translateY(-4px)',
                          borderColor: system.color,
                          boxShadow: `0 12px 24px -4px ${system.color}25`
                        } : {},
                        opacity: isAllowed ? 1 : 0.65,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <CardActionArea 
                        onClick={() => handleSystemClick(system)}
                        disabled={!isAllowed}
                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', p: 3 }}
                        component="div"
                      >
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                          <Box 
                            sx={{ 
                              p: 1.5, 
                              bgcolor: `${system.color}15`, 
                              color: system.color,
                              borderRadius: 3,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {React.cloneElement(system.icon, { sx: { fontSize: 28 } })}
                          </Box>
                          
                          {isAllowed ? (
                            <Chip 
                              label="Active" 
                              size="small" 
                              sx={{ 
                                bgcolor: 'rgba(5, 150, 105, 0.1)', 
                                color: '#059669', 
                                fontWeight: 700, 
                                fontSize: '0.6875rem' 
                              }} 
                            />
                          ) : (
                            <Chip 
                              icon={<Lock sx={{ fontSize: '0.8rem !important', color: '#64748b !important' }} />} 
                              label="Restricted" 
                              size="small" 
                              sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, fontSize: '0.6875rem' }} 
                            />
                          )}
                        </Box>

                        <Typography variant="h6" component="div" fontWeight={700} color="#0f172a" sx={{ fontSize: '1rem', mb: 0.75 }}>
                          {system.name}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, fontSize: '0.8125rem', lineHeight: 1.55, mb: 3 }}>
                          {system.description}
                        </Typography>

                        <Button 
                          endIcon={isAllowed ? <ArrowForward /> : <Lock />} 
                          fullWidth 
                          variant="outlined"
                          disabled={!isAllowed}
                          sx={{ 
                            justifyContent: 'space-between',
                            borderRadius: 2,
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            color: isAllowed ? system.color : '#94a3b8',
                            borderColor: isAllowed ? `${system.color}50` : '#e2e8f0',
                            '&:hover': {
                              bgcolor: isAllowed ? `${system.color}10` : 'transparent',
                              borderColor: system.color
                            }
                          }}
                        >
                          {isAllowed ? 'Open Workspace' : 'Restricted'}
                        </Button>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        )}

        <Box 
          sx={{ 
            mt: { xs: 4, md: 6 }, 
            mb: { xs: 8, md: 0 }, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            color: 'text.secondary', 
            textAlign: 'center',
            mx: 'auto',
            maxWidth: 480,
            px: 2.5,
            py: 1.2,
            borderRadius: 4,
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0'
          }}
        >
          <Info fontSize="small" sx={{ color: '#059669', flexShrink: 0 }} />
          <Typography variant="caption" fontWeight={600} color="#475569">
            Role-governed municipal workspace • Contact Administrator for permission adjustments
          </Typography>
        </Box>
      </Container>

      {/* Sticky Mobile Bottom Navigation */}
      <MobileBottomBar
        items={[
          { name: 'Water', icon: <WaterDrop />, path: '/portal/water-billing/dashboard' },
          { name: 'RPT', icon: <Apartment />, path: '/portal/property-tax/dashboard' },
          { name: 'BPLO', icon: <BusinessCenter />, path: '/portal/business-permit/dashboard' },
          { name: 'e-Logbook', icon: <MenuBook />, path: '/portal/e-logbook/home' }
        ]}
        currentPath="/portal"
        onNavigate={(path) => navigate(path)}
        onToggleMenu={handleMenuOpen}
      />
      
      <EventGreetingModal 
        open={showGreeting} 
        onClose={() => setShowGreeting(false)}
        event={activeEvent}
      />
      
      <ManageEventsDialog 
        open={showManageEvents} 
        onClose={() => setShowManageEvents(false)} 
      />

      <ProfileModal
        user={user}
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onUpdate={handleProfileUpdate}
      />
    </Box>
  );
};

export default SystemsPortal;

